// Doppelgangers by default will not have access to Digits Employee features to
// test them before customers have access. However for debugging purposes we will
// want to turn this on to investigate bugs that customers might not be yet experiencing.
import { storage } from "@digits-shared/helpers/storage/storage"

export enum AccessLevel {
  Customer = "Customer",
  Dashboard = "Dashboard",
  FullAccess = "FullAccess",
}

export type AccessLevelType = keyof typeof AccessLevel

interface ConfigureDoppelgangerAccessLevelOptions {
  resolveStorageKey?: (baseKey: string) => string
  readAccessLevelOverride?: () => string | undefined
}

const defaultResolveStorageKey = (baseKey: string) => baseKey
const defaultReadAccessLevelOverride = () => undefined

let resolveStorageKey: (baseKey: string) => string = defaultResolveStorageKey
let readAccessLevelOverride: () => string | undefined = defaultReadAccessLevelOverride

export function configureDoppelgangerAccessLevel({
  resolveStorageKey: nextResolveStorageKey = defaultResolveStorageKey,
  readAccessLevelOverride: nextReadAccessLevelOverride = defaultReadAccessLevelOverride,
}: ConfigureDoppelgangerAccessLevelOptions) {
  resolveStorageKey = nextResolveStorageKey
  readAccessLevelOverride = nextReadAccessLevelOverride
}

export default class DoppelgangerAccessLevel {
  static ACCESS_LEVEL_KEY = "digits.dg.level"

  private accessLevel = AccessLevel.FullAccess

  private static storageKey() {
    return resolveStorageKey(DoppelgangerAccessLevel.ACCESS_LEVEL_KEY)
  }

  constructor() {
    const overrideLevel = readAccessLevelOverride() as AccessLevelType | undefined
    const accessLevel =
      overrideLevel && AccessLevel[overrideLevel]
        ? overrideLevel
        : (storage.session.getItem(DoppelgangerAccessLevel.storageKey()) as AccessLevelType)
    if (accessLevel && AccessLevel[accessLevel]) {
      this.accessLevel = AccessLevel[accessLevel]
    }
  }

  get hasCustomerAccess() {
    return this.accessLevel === AccessLevel.Customer || this.hasDashboardAccess
  }

  get hasDashboardAccess() {
    return this.accessLevel === AccessLevel.Dashboard || this.hasFullAccess
  }

  get hasFullAccess() {
    return this.accessLevel === AccessLevel.FullAccess
  }

  get level() {
    return this.accessLevel
  }

  set level(level: AccessLevel) {
    this.accessLevel = level
    storage.session.setItem(DoppelgangerAccessLevel.storageKey(), level)
  }
}
